// Sort configuration for the workbench Task Manager and Notebook tabs.
//
// Unlike resource categories, the workbench uses a single flat "Sort by" list
// (no General / Category-Specific split). The available options depend on both
// the workbench scope (personal vs vertical) and the item type (task vs note).
//
// To persist, the chosen order is written via update_order_dashboard (the same
// endpoint drag-and-drop already uses), so no backend change is required.

import { byString, byNumber, date } from './sortComparators';

// Returns a flat list of `{ key, label, comparator }` for the given context.
//   isVertical : false = personal workbench, true = vertical workbench
//   type       : 'task' | 'note'
//   verticals  : the user's verticals (from useCategory) — used to resolve the
//                associated vertical's name for personal Vertical Name sorting.
export const getWorkbenchSortOptions = ({ isVertical, type, verticals = [] }) => {
  const nameLabel = type === 'task' ? 'Task Name' : 'Note Name';

  const nameSort = [
    { key: 'name-asc', label: `${nameLabel} (A-Z)`, comparator: byString((i) => i.title, 'asc') },
    { key: 'name-desc', label: `${nameLabel} (Z-A)`, comparator: byString((i) => i.title, 'desc') },
  ];

  // Personal items carry an associatedVerticalId; resolve it to the vertical's name.
  const verticalName = (item) =>
    verticals.find((v) => v.id === item.associatedVerticalId)?.name;
  const verticalNameSort = [
    { key: 'vertical-asc', label: 'Vertical Name (A-Z)', comparator: byString(verticalName, 'asc') },
    { key: 'vertical-desc', label: 'Vertical Name (Z-A)', comparator: byString(verticalName, 'desc') },
  ];

  const dueDateSort = [
    { key: 'due-soonest', label: 'Due Date (Soonest First)', comparator: byNumber(date('deadline'), 'asc') },
    { key: 'due-latest', label: 'Due Date (Latest First)', comparator: byNumber(date('deadline'), 'desc') },
  ];

  const assignedToSort = [
    { key: 'assigned-asc', label: 'Assigned To (A-Z)', comparator: byString((i) => i.assignedTo, 'asc') },
    { key: 'assigned-desc', label: 'Assigned To (Z-A)', comparator: byString((i) => i.assignedTo, 'desc') },
  ];

  const addedSort = [
    { key: 'recently-added', label: 'Recently Added', comparator: byNumber(date('created_at'), 'desc') },
    { key: 'oldest-added', label: 'Oldest Added', comparator: byNumber(date('created_at'), 'asc') },
  ];

  if (!isVertical && type === 'task') return [...nameSort, ...verticalNameSort, ...dueDateSort];
  if (!isVertical && type === 'note') return [...nameSort, ...verticalNameSort];
  if (isVertical && type === 'task') return [...nameSort, ...assignedToSort, ...dueDateSort];
  if (isVertical && type === 'note') return [...nameSort, ...addedSort];
  return [];
};
